import { useEffect, useMemo, useState } from 'react';

import { useAppState } from '../../../AppStateProvider';
import type { ContactDetailApiKey, ContactDetailsFilterDto } from '../../../domain/contact';
import { isEmptyContactFilter } from '../../../domain/contact';
import type { Country } from '../../../domain/country';
import { appStore, INITIAL_CONTACTS_FILTER } from '../../../services/appStore';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';

export enum ContactFilter {
  countries = 'countries',
  contactDetails = 'contactDetails',
  status = 'status',
}

export const ContactFilterTrackingName: Record<ContactFilter, string> = {
  [ContactFilter.countries]: 'location',
  [ContactFilter.contactDetails]: 'contact details',
  [ContactFilter.status]: 'status',
};

export const useContactFilter = ({ contactsNumber }: { contactsNumber: number }) => {
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [openedFilter, setOpenedFilter] = useState<ContactFilter | null>(null);
  const { contactFilters } = useAppState();

  const [selectedCountries, setSelectedCountries] = useState<Country[]>(contactFilters.includeCountries);
  const [selectedContactDetails, setSelectedContactDetails] = useState<ContactDetailsFilterDto>(
    contactFilters.contactDetails
  );
  const [contactStatus, setContactStatus] = useState(contactFilters.status);

  const dirtyFilters = useMemo(() => {
    const dirtyFilters = [];
    if (selectedCountries.length > 0) {
      dirtyFilters.push(ContactFilter.countries);
    }
    if (!isEmptyContactFilter(selectedContactDetails)) {
      dirtyFilters.push(ContactFilter.contactDetails);
    }
    if (Object.values(contactStatus).some(Boolean)) {
      dirtyFilters.push(ContactFilter.status);
    }

    return dirtyFilters;
  }, [selectedCountries, selectedContactDetails, contactStatus]);

  useEffect(() => {
    setContactStatus(contactFilters.status);
  }, [contactFilters.status]);

  const appliedFiltersCount = dirtyFilters.length;

  const getSelectedFiltersList = () => {
    return dirtyFilters.map((filter) => ContactFilterTrackingName[filter]).join(', ');
  };

  const trackFilterEvents = (action: string) =>
    dispatchTrackEventWithLocation(
      'extension filters',
      action,
      `${contactsNumber}/${getSelectedFiltersList()}/${contactFilters.persona ? 1 : 0}`
    );

  const handleClickOnDetails = (detail: ContactDetailApiKey) => {
    setSelectedContactDetails({
      ...selectedContactDetails,
      [detail]: !selectedContactDetails[detail],
    });
  };

  const handleApplyFilters = () => {
    trackFilterEvents('show results');
    appStore.setContactsIncludeCountries(selectedCountries);
    appStore.setContactsDetails(selectedContactDetails);
    appStore.setContactsStatusFilterValue(contactStatus);
    setIsFiltersModalOpen(false);
  };

  const handleClickOnClearFilters = () => {
    trackFilterEvents('clear filters');
    appStore.setContactsIncludeCountries([]);
    appStore.setContactsDetails(INITIAL_CONTACTS_FILTER);
    appStore.setContactsStatusFilterValue({});
    setSelectedCountries([]);
    setSelectedContactDetails(INITIAL_CONTACTS_FILTER);
    setContactStatus({});
    setIsFiltersModalOpen(false);
  };

  const toggleFilter = (filter: ContactFilter) => {
    setOpenedFilter((openedFilter) => (openedFilter === filter ? null : filter));
    if (openedFilter !== filter) {
      trackFilterEvents(`expaned ${ContactFilterTrackingName[filter]}`);
    }
  };

  const resetFilters = () => {
    setSelectedCountries(contactFilters.includeCountries);
    setSelectedContactDetails(contactFilters.contactDetails);
    setContactStatus(contactFilters.status);
  };

  return {
    isFiltersModalOpen,
    setIsFiltersModalOpen,
    openedFilter,
    toggleFilter,
    selectedCountries,
    setSelectedCountries,
    selectedContactDetails,
    contactStatus,
    setContactStatus,
    handleClickOnDetails,
    appliedFiltersCount,
    handleApplyFilters,
    handleClickOnClearFilters,
    trackFilterEvents,
    resetFilters,
  };
};
